import { useEffect, useMemo, useRef, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import { isEqual } from 'lodash'
import { setCanvasLoading } from '@/store/slices/canvas/canvas/actions'
import { selectCurrentProjectId, selectProjectSlideOrder } from '@/store/slices/projects/selectors'
import { updateCurrentProject } from '@/store/slices/projects/thunks'
import {
  deleteMultipleSlides,
  deleteSlide,
  duplicateSlide,
  updateSlideName
} from '@/store/slices/slides/thunks'
import { selectSlide } from '@store/slices/slides/actions'
import { selectCurrentSlideId, selectAllSlides } from '@store/slices/slides/selectors'
import { AppDispatch } from '@store/store'
import { useThumbnails } from '@/hooks/useThumbnail'
import Icon from '@components/icon/Icon'
import i18n from 'i18next'
import SlideActionModal from './slide-action-modal'
import SlideTemplating from './slide-templating'
import SortableSlide from './sortable-slide'
import { HandleConfirmPrompt, SlideActionMode } from './slide-panel-types'
import SlideViewToggle from './slide-view-toggle'
import { useSlideViewMode } from './use-slide-view-mode'
import './styles.less'

const Slides = () => {
  const dispatch: AppDispatch = useDispatch()
  const projectId = useSelector(selectCurrentProjectId)
  const allSlideIds = useSelector(selectProjectSlideOrder)
  const slideEntities = useSelector(selectAllSlides)

  const idRef = useRef(projectId)
  const actionInFlightRef = useRef(false)
  const currentSlideId = useSelector(selectCurrentSlideId)

  const [updateHappened, setUpdateHappened] = useState(false)
  const { viewMode, setViewMode } = useSlideViewMode()

  useEffect(() => {
    if (allSlideIds) {
      if (currentSlideId === allSlideIds[0]) {
        setUpdateHappened(true)
      }
    }
    if (currentSlideId) {
      setSelectedSlideIds([currentSlideId])
    }
  }, [allSlideIds, currentSlideId])

  const [modalVisible, setModalVisible] = useState(false)
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [slideId, setSlideId] = useState(null)
  const [mode, setMode] = useState<SlideActionMode | null>(null)
  const [slideName, setSlideName] = useState('')
  const [slideIds, setSlideIds] = useState<string[]>([])
  const [selectedSlideIds, setSelectedSlideIds] = useState([])
  const { thumbnails } = useThumbnails(allSlideIds, projectId)
  const { search } = useLocation()
  const history = useHistory()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  const searchParams = useMemo(() => new URLSearchParams(search), [search])

  useEffect(() => {
    if (
      (!searchParams.get('slide') && idRef.current !== projectId) ||
      (!searchParams.get('slide') && updateHappened)
    ) {
      if (allSlideIds?.length > 0) {
        searchParams.set('slide', allSlideIds[0].toString())
        history.replace({ search: searchParams.toString() })
      }
    } else {
      if (
        Number(searchParams.get('slide')) !== currentSlideId &&
        Number(searchParams.get('slide')) !== 0 &&
        allSlideIds?.length > 0
      ) {
        history.replace({ search: searchParams.toString() })
        if (allSlideIds.includes(Number(searchParams.get('slide')))) {
          dispatch(selectSlide(Number(searchParams.get('slide'))))
        }
      }
      if (!allSlideIds?.includes(Number(searchParams.get('slide')))) {
        searchParams.delete('slide')
        history.replace({ search: searchParams.toString() })
      }
    }
  }, [allSlideIds, currentSlideId, dispatch, history, projectId, searchParams, updateHappened])

  const onOrderChange = async (reorderedSlideIds: string[]) => {
    setSlideIds(reorderedSlideIds)
    const payload = { slideOrder: reorderedSlideIds.map(id => Number(id)) }
    if (!isEqual(slideIds, reorderedSlideIds)) {
      await dispatch(updateCurrentProject({ projectId, payload }))
    }
  }

  const handleDragEnd = event => {
    const { active, over } = event
    try {
      const newValues = slideIds.slice(0)
      const oldIndex = newValues.findIndex(id => id === active.id)
      const newIndex = newValues.findIndex(id => id === over.id)
      onOrderChange(arrayMove(newValues, oldIndex, newIndex))
    } catch (error) {
      console.error(error.message)
    }
  }

  useEffect(() => {
    if (Array.isArray(allSlideIds)) {
      setSlideIds(allSlideIds.filter(() => true).map(id => id.toString()))
    }
  }, [allSlideIds])

  const handleSlideAction = async () => {
    if (actionInFlightRef.current) return

    actionInFlightRef.current = true
    setIsSubmitting(true)
    dispatch(setCanvasLoading(true))

    try {
      if (mode === 'delete') {
        if (selectedSlideIds.length > 1 && selectedSlideIds.includes(slideId)) {
          await dispatch(deleteMultipleSlides(selectedSlideIds))
        } else {
          await dispatch(deleteSlide(slideId))
        }
      } else if (mode === 'duplicate') {
        await dispatch(duplicateSlide(slideId))
      } else if (mode === 'name') {
        await dispatch(updateSlideName({ slideId, name: slideName }))
      }
    } finally {
      dispatch(setCanvasLoading(false))
      setIsConfirmModalVisible(false)
      setIsSubmitting(false)
      actionInFlightRef.current = false
      setSlideName('')
    }
  }

  const handleConfirmPrompt: HandleConfirmPrompt = (mode, slideId, callback) => {
    setSlideId(slideId)
    setMode(mode)

    if (mode === 'name') {
      const currentSlide = slideEntities[slideId]
      const existingDescription = currentSlide?.description || ''
      setSlideName(existingDescription)
    }

    setIsConfirmModalVisible(true)
    if (callback) callback()
  }

  const onSelectClick = (id, isShiftKey?: boolean) => {
    if (isShiftKey) {
      const selectedSlideIndex = allSlideIds.findIndex(el => el === id)
      const startIndex = allSlideIds.findIndex(el => el === selectedSlideIds[0])

      if (selectedSlideIndex > startIndex) {
        const slice = allSlideIds.slice(startIndex, selectedSlideIndex + 1)
        setSelectedSlideIds([...slice])
      } else {
        const startIndexReversed = allSlideIds.findIndex(
          el => el === selectedSlideIds[selectedSlideIds.length - 1]
        )
        const slice = allSlideIds.slice(selectedSlideIndex, startIndexReversed + 1)
        setSelectedSlideIds([...slice])
      }
    } else {
      if (selectedSlideIds.includes(id)) {
        setSelectedSlideIds(prev => prev.filter(el => el !== id))
      } else {
        setSelectedSlideIds(prev => [...prev, id])
      }
    }
  }

  const getModalTitle = () => {
    if (mode === 'delete') return i18n.t('editor.left-panel.slides.delete')
    if (mode === 'duplicate') return i18n.t('editor.left-panel.slides.duplicate')
    if (mode === 'name') {
      const currentSlide = slideEntities[slideId]
      const hasDescription = currentSlide?.description && currentSlide.description.trim() !== ''
      return hasDescription
        ? i18n.t('editor.left-panel.slides.edit-description-title')
        : i18n.t('editor.left-panel.slides.description-title')
    }
    return ''
  }

  return (
    <>
      <SlideActionModal
        title={getModalTitle()}
        visible={isConfirmModalVisible}
        mode={mode}
        slideName={slideName}
        onChange={setSlideName}
        onOk={handleSlideAction}
        isSubmitting={isSubmitting}
        onCancel={() => {
          setIsConfirmModalVisible(false)
          setSlideName('')
        }}
      />
      <div className="slides-panel">
        <div id="slides-title" className="ant-tabs-tabpane-header slides-header">
          <span className="slides-header-title">{i18n.t('editor.left-panel.slides.title')}</span>
          <SlideViewToggle viewMode={viewMode} onChange={setViewMode} />
        </div>
        <Scrollbars id="slides-scrollbar" style={{ height: 'auto', flex: '1 1 0', minHeight: 0 }}>
          <div
            id="slides-slides"
            className={`slides ${viewMode === 'names' ? 'slides-names' : ''}`}
          >
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext items={slideIds}>
                {slideIds.map((slideId, index) => (
                  <SortableSlide
                    id={slideId}
                    index={index}
                    currentId={currentSlideId}
                    key={slideId}
                    thumbnail={thumbnails[slideId]}
                    slideDescription={slideEntities[Number(slideId)]?.description}
                    viewMode={viewMode}
                    handleConfirmPrompt={handleConfirmPrompt}
                    onSelectClick={onSelectClick}
                    selectedSlideIds={selectedSlideIds}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </Scrollbars>
        <div className="slide-button-add-container">
          <button
            type="button"
            id="slides-add-button"
            className="slide-button-add"
            onClick={() => setModalVisible(true)}
          >
            <span className="slide-button-add-icon">
              <Icon name="plus" size={0.8} />
            </span>
            <span>{i18n.t('editor.left-panel.slides.add')}</span>
          </button>
        </div>
      </div>
      <SlideTemplating isVisible={modalVisible} setIsVisible={setModalVisible} />
    </>
  )
}
export default Slides
